import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../cx';

/**
 * D-09 §15.14 — 기간 선택 (Date Range).
 *
 * 알약 트리거(Figma DS `date filter` 2269:955) + 2개월 팝오버(z-index `--z-dropdown`).
 * 단일 날짜는 `DatePicker` 를 쓴다.
 *
 * **적용 버튼이 없다.** 첫 클릭이 시작일, 두 번째 클릭이 종료일이고 그때 닫힌다 —
 * 한쪽만 고르고 닫혀 다시 열어야 하는 문제가 애초에 생기지 않으므로 확정 단계가 필요 없다.
 */

const WEEK_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const pad = (n: number) => String(n).padStart(2, '0');
/** 트리거 표기 — 연도를 지우면 3개월 전 기간과 작년 기간이 같아 보인다 */
const fmt = (d: Date) => `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
/** holidays / minDate 대조용 키 */
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** 앞쪽 빈칸 + 날짜들 + 뒤쪽 빈칸(7의 배수 맞춤) */
function daysGrid(year: number, month: number): (Date | null)[] {
  const cells: (Date | null)[] = Array(new Date(year, month, 1).getDay()).fill(null);
  const last = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= last; i++) cells.push(new Date(year, month, i));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export interface DateRangeValue {
  start: Date | null;
  end: Date | null;
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  /** 트리거 높이 — 입력 폼과 같은 control-height 스케일 (§15.2 · 사이즈 색인) */
  size?: 'sm' | 'md' | 'lg';
  /** 미선택 상태 표기 */
  placeholder?: string;
  /** 빠른선택 칩(오늘·이번 주·최근 30일·최근 90일·전체). 화면이 이미 기간 프리셋을 갖고 있으면 false */
  presets?: boolean;
  /** 공휴일 'YYYY-MM-DD' — 일요일과 같은 색으로 표시한다. 조회는 소비처가 한다(ui-core 는 네트워크를 모른다) */
  holidays?: Set<string>;
  /** 이 날짜 이전은 선택 불가 */
  minDate?: Date;
  /**
   * 이 기간이 실제로 조회에 적용 중인지. 옆에 기간 프리셋 칩이 함께 놓이는 화면에서만 의미가 있다 —
   * 트리거는 값을 늘 보여주므로, 프리셋이 이겼는지 직접설정이 이겼는지는 이 표시가 가른다.
   */
  active?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function DateRangePicker({
  value,
  onChange,
  size = 'md',
  placeholder = '전체 기간',
  presets = true,
  holidays,
  minDate,
  active,
  disabled,
  className = '',
  'aria-label': ariaLabel = '기간 선택',
}: DateRangePickerProps) {
  const { start, end } = value;
  const today = startOfDay(new Date());
  const min = minDate ? iso(startOfDay(minDate)) : null;

  const [open, setOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [pickingEnd, setPickingEnd] = useState(false);
  const [viewYear, setViewYear] = useState(() => start?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(() => start?.getMonth() ?? today.getMonth());
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setPickingEnd(false);
    setHoverDate(null);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current?.contains(t) && !triggerRef.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  // 팝오버 폭은 내용이 정하므로(고정값이 없다) 열린 뒤 실측해서 화면 밖으로 나가는 만큼만 당긴다.
  useLayoutEffect(() => {
    if (!open || !popRef.current) return;
    const w = popRef.current.offsetWidth;
    setPos((p) => {
      const max = window.innerWidth - w - 8;
      return p.left > max ? { ...p, left: Math.max(8, max) } : p;
    });
  }, [open]);

  const handleTrigger = () => {
    if (open) return close();
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
    if (start) {
      setViewYear(start.getFullYear());
      setViewMonth(start.getMonth());
    }
    setPickingEnd(!!start && !end);
    setHoverDate(null);
    setOpen(true);
  };

  const handleDayClick = (day: Date) => {
    if (!pickingEnd || !start) {
      onChange({ start: day, end: null });
      setPickingEnd(true);
      return;
    }
    onChange(day < start ? { start: day, end: start } : { start, end: day });
    close();
  };

  const quick = (s: Date | null, e: Date | null) => {
    onChange({ start: s, end: e });
    close();
  };

  /**
   * 프리셋이 만드는 기간. 실행 함수가 아니라 기간을 반환하는 이유 —
   * 클릭 실행과 "지금 값이 이 프리셋인가" 대조가 **같은 정의 하나**를 써야 한다.
   * 둘을 따로 적으면 나중에 한쪽만 고쳐져 칩이 거짓말을 한다.
   */
  const QUICK: { label: string; range: () => DateRangeValue }[] = [
    { label: '오늘', range: () => ({ start: today, end: today }) },
    {
      label: '이번 주',
      range: () => {
        const sun = new Date(today);
        sun.setDate(today.getDate() - today.getDay());
        const sat = new Date(sun);
        sat.setDate(sun.getDate() + 6);
        return { start: sun, end: sat };
      },
    },
    { label: '최근 30일', range: () => { const s = new Date(today); s.setDate(today.getDate() - 29); return { start: s, end: today }; } },
    { label: '최근 90일', range: () => { const s = new Date(today); s.setDate(today.getDate() - 89); return { start: s, end: today }; } },
    { label: '전체', range: () => ({ start: null, end: null }) },
  ];

  /**
   * 현재 값이 이 프리셋과 같은 기간인가 — `전체`는 null 끼리만 일치한다.
   * 클릭 이력을 기억하지 않고 값에서 파생하므로, 달력에서 손으로 고른 기간이
   * 프리셋과 같으면 그 칩도 켜진다(리마운트해도 표시가 살아 있다).
   * 시작일만 고른 중간 상태(end === null)에서는 어느 프리셋도 매치되지 않는다.
   */
  const isPreset = (r: DateRangeValue) =>
    (r.start === null ? start === null : !!start && sameDay(r.start, start)) &&
    (r.end === null ? end === null : !!end && sameDay(r.end, end));

  /** 어느 프리셋과도 안 맞는 기간 — `직접 설정` 표시 칩이 이때만 나타난다 */
  const noPreset = !QUICK.some((b) => isPreset(b.range()));

  // 종료일을 고르는 중이면 커서를 따라 범위를 미리 칠한다
  const preview = pickingEnd && start && hoverDate
    ? (hoverDate < start ? { s: hoverDate, e: start } : { s: start, e: hoverDate })
    : { s: start, e: end };

  const moveMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
  const nextM = (viewMonth + 1) % 12;

  const text = start && end ? `${fmt(start)} - ${fmt(end)}`
    : start ? `${fmt(start)} - 선택 중...`
    : placeholder;

  /** nav: 라벨 줄 끝에 놓을 월 이동 버튼 — 왼쪽 달은 '이전', 오른쪽 달은 '다음'.
   *  버튼이 없는 쪽에도 같은 치수의 빈 칸을 둬야 라벨이 가운데에 선다. */
  const renderMonth = (year: number, month: number, nav: 'prev' | 'next') => (
    <div className="daterange-month">
      <div className="daterange-label">
        {nav === 'prev' ? (
          <button type="button" className="daterange-nav" aria-label="이전 달" onClick={() => moveMonth(-1)}>‹</button>
        ) : <span className="daterange-nav-space" aria-hidden="true" />}
        <span>{year}. {month + 1}</span>
        {nav === 'next' ? (
          <button type="button" className="daterange-nav" aria-label="다음 달" onClick={() => moveMonth(1)}>›</button>
        ) : <span className="daterange-nav-space" aria-hidden="true" />}
      </div>
      <div className="daterange-grid">
        {WEEK_DAYS.map((d) => <span key={d} className="dow">{d}</span>)}
      </div>
      <div className="daterange-grid">
        {daysGrid(year, month).map((day, i) => {
          if (!day) return <div key={i} className="daterange-cell disabled" />;

          const key = iso(day);
          const blocked = !!min && key < min;
          const isStart = !!preview.s && sameDay(day, preview.s);
          const isEnd = !!preview.e && sameDay(day, preview.e);
          const single = !!preview.s && !!preview.e && sameDay(preview.s, preview.e);
          const col = i % 7;

          return (
            <div
              key={i}
              role="button"
              tabIndex={blocked ? -1 : 0}
              aria-disabled={blocked || undefined}
              onClick={() => !blocked && handleDayClick(day)}
              onKeyDown={(e) => {
                if (blocked || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                handleDayClick(day);
              }}
              onMouseEnter={() => pickingEnd && !blocked && setHoverDate(day)}
              onMouseLeave={() => setHoverDate(null)}
              className={cx(
                'daterange-cell',
                blocked && 'disabled',
                !single && !!preview.s && !!preview.e && day > preview.s && day < preview.e && 'in-range',
                !single && isStart && !!preview.e && 'range-start',
                !single && isEnd && !!preview.s && 'range-end',
              )}
            >
              <span
                className={cx(
                  'daterange-day',
                  (isStart || isEnd) && 'sel',
                  sameDay(day, today) && 'today',
                  col === 0 || holidays?.has(key) ? 'sun' : col === 6 && 'sat',
                )}
              >
                {day.getDate()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cx('daterange', size !== 'md' && `daterange-${size}`, open && 'is-open', active && 'is-active', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTrigger}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cx('daterange-trigger', !start && 'is-empty')}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {text}
      </button>

      {open && (
        <div ref={popRef} className="daterange-pop" style={{ top: pos.top, left: pos.left }} role="dialog" aria-label={ariaLabel}>
          <div className="daterange-months">
            {renderMonth(viewYear, viewMonth, 'prev')}
            {renderMonth(nextY, nextM, 'next')}
          </div>

          {presets && (
            <>
              <div className="daterange-divider" />
              <div className="daterange-presets">
                {QUICK.map((b) => {
                  const r = b.range();
                  const on = isPreset(r);
                  return (
                    <button
                      key={b.label}
                      type="button"
                      className={cx('fchip', on && 'active')}
                      aria-pressed={on}
                      onClick={() => quick(r.start, r.end)}
                    >
                      {b.label}
                    </button>
                  );
                })}
                {/* 상태 표시일 뿐 누를 수 있는 동작이 아니라 button 이 아니다 — 프리셋과 안 맞을 때만 나타난다 */}
                {noPreset && <span className="fchip fchip-static active">직접 설정</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * D-09 §15.14 — 인라인 2개월 범위 달력 (Date Range Calendar).
 *
 * `DateRangePicker` 의 팝오버 안쪽 달력을 트리거 없이 꺼낸 것 — 팝오버·패널 안에 그대로 놓고, [적용] 같은 확정 단계는 소비처가 따로 둔다.
 * `DateRangePicker` 와 다른 점 — ① 둘째 클릭에서 닫히지 않는다(닫을 대상이 없다) ② 선택 가능 범위를 `minDate`·`maxDate` 둘 다 줄 수 있다
 * ③ 선택 중에 시작일보다 앞 날짜를 누르면 두 날짜를 맞바꾸지 않고 그 날짜를 **새 시작일**로 다시 고른다.
 *
 * 첫 클릭은 `{ start, end: null }` 로, 둘째 클릭은 `{ start, end }` 로 알린다. 소비처가 시작일을 보정해(예: 비작업일 → 다음 작업일)
 * 값으로 되돌려 주면 그 값이 `value.start` 로 쓰인다.
 */
export interface DateRangeCalendarProps {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  /** 비작업일 'YYYY-MM-DD' — 일요일과 같은 색으로 표시한다. 조회는 소비처가 한다 */
  holidays?: Set<string>;
  /** 이 날짜 이전은 선택 불가 */
  minDate?: Date;
  /** 이 날짜 이후는 선택 불가 */
  maxDate?: Date;
  className?: string;
  'aria-label'?: string;
}

export function DateRangeCalendar({
  value,
  onChange,
  holidays,
  minDate,
  maxDate,
  className = '',
  'aria-label': ariaLabel = '기간 선택 달력',
}: DateRangeCalendarProps) {
  const { start, end } = value;
  const today = startOfDay(new Date());
  const min = minDate ? iso(startOfDay(minDate)) : null;
  const max = maxDate ? iso(startOfDay(maxDate)) : null;

  const [pickingEnd, setPickingEnd] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [viewYear, setViewYear] = useState(() => (start ?? today).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (start ?? today).getMonth());

  // 시작일이 밖에서 바뀌어(날짜 입력 등) 보이는 두 달을 벗어나면 그 달로 옮긴다 — 달력에서 직접 고른 날짜는 안 움직인다
  const startTime = start?.getTime();
  useEffect(() => {
    if (!start) return;
    const idx = start.getFullYear() * 12 + start.getMonth();
    const first = viewYear * 12 + viewMonth;
    if (idx < first || idx > first + 1) {
      setViewYear(start.getFullYear());
      setViewMonth(start.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  const handleDayClick = (day: Date) => {
    if (!pickingEnd || !start || day < start) {
      onChange({ start: day, end: null });
      setPickingEnd(true);
      return;
    }
    onChange({ start, end: day });
    setPickingEnd(false);
  };

  // 종료일을 고르는 중이면 커서를 따라 범위를 미리 칠한다(시작일보다 앞은 새 시작일 후보라 칠하지 않는다)
  const preview = pickingEnd && start && hoverDate && hoverDate >= start
    ? { s: start, e: hoverDate }
    : { s: start, e: end };

  const moveMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
  const nextM = (viewMonth + 1) % 12;

  const renderMonth = (year: number, month: number, nav: 'prev' | 'next') => (
    <div className="daterange-month">
      <div className="daterange-label">
        {nav === 'prev' ? (
          <button type="button" className="daterange-nav" aria-label="이전 달" onClick={() => moveMonth(-1)}>‹</button>
        ) : <span className="daterange-nav-space" aria-hidden="true" />}
        <span>{year}. {month + 1}</span>
        {nav === 'next' ? (
          <button type="button" className="daterange-nav" aria-label="다음 달" onClick={() => moveMonth(1)}>›</button>
        ) : <span className="daterange-nav-space" aria-hidden="true" />}
      </div>
      <div className="daterange-grid">
        {WEEK_DAYS.map((d) => <span key={d} className="dow">{d}</span>)}
      </div>
      <div className="daterange-grid">
        {daysGrid(year, month).map((day, i) => {
          if (!day) return <div key={i} className="daterange-cell disabled" />;

          const key = iso(day);
          const blocked = (!!min && key < min) || (!!max && key > max);
          const isStart = !!preview.s && sameDay(day, preview.s);
          const isEnd = !!preview.e && sameDay(day, preview.e);
          const single = !!preview.s && !!preview.e && sameDay(preview.s, preview.e);
          const col = i % 7;

          return (
            <div
              key={i}
              role="button"
              tabIndex={blocked ? -1 : 0}
              aria-disabled={blocked || undefined}
              onClick={() => !blocked && handleDayClick(day)}
              onKeyDown={(e) => {
                if (blocked || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                handleDayClick(day);
              }}
              onMouseEnter={() => pickingEnd && !blocked && setHoverDate(day)}
              onMouseLeave={() => setHoverDate(null)}
              className={cx(
                'daterange-cell',
                blocked && 'disabled',
                !single && !!preview.s && !!preview.e && day > preview.s && day < preview.e && 'in-range',
                !single && isStart && !!preview.e && 'range-start',
                !single && isEnd && !!preview.s && 'range-end',
              )}
            >
              <span
                className={cx(
                  'daterange-day',
                  (isStart || isEnd) && 'sel',
                  sameDay(day, today) && 'today',
                  col === 0 || holidays?.has(key) ? 'sun' : col === 6 && 'sat',
                )}
              >
                {day.getDate()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cx('daterange-inline', className)} role="group" aria-label={ariaLabel}>
      <div className="daterange-months">
        {renderMonth(viewYear, viewMonth, 'prev')}
        {renderMonth(nextY, nextM, 'next')}
      </div>
    </div>
  );
}
